import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

export type TeamRole = "viewer" | "editor" | "admin";

/** A Member or pending invite (GET /dashboard/api/members). */
export interface Teammate {
  id: string;
  email: string;
  role: TeamRole;
  status: "active" | "invited";
  last_login_at: string | null;
  /** The Member looking at the page. */
  you: boolean;
}

export const TEAM_KEY = ["team"];

export function useTeam() {
  return useQuery({
    queryKey: TEAM_KEY,
    queryFn: () => api.get("members").json<{ members: Teammate[] }>(),
  });
}

function useTeamAction<TArgs, TResult>(call: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: () => qc.invalidateQueries({ queryKey: TEAM_KEY }),
  });
}

export function useInvite() {
  return useTeamAction((invite: { email: string; role: TeamRole }) =>
    api.post("members", { json: invite }).json<Teammate & { invite_sent: boolean }>(),
  );
}

export function useResendInvite() {
  return useTeamAction((id: string) => api.post(`members/${id}/resend`).json<Teammate>());
}

export function useRemoveTeammate() {
  return useTeamAction((id: string) => api.delete(`members/${id}`).json<{ removed: string }>());
}
